import { runText } from "./run.js";
import type { Count, Effort, Hurdles, Item, Load, Rest, RestValue, Row } from "./types.js";

/** One item as one line of his notation, without a bullet (DESIGN.md §25.2). */
export function renderItem(item: Item): string {
  // A sled or the 1080 makes the run resisted, as the reader takes it.
  const parts = item.run !== undefined && item.load !== undefined ? { ...item.run, kind: "resisted" as const } : item.run;
  const run = item.kind === "run" && parts !== undefined ? { name: runText(parts, item.name, [...(item.cues ?? []), ...(item.dashNotes ?? []), ...(item.load !== undefined ? ["sled"] : [])]) } : null;
  // `3x (4 Flying hills (tempo) - 30m fly distance)`: what is repeated stays in his brackets.
  const nested = item.amount?.bracketed === true && (item.rounds !== undefined || item.sets !== undefined);
  const outer = nested ? [item.rounds, item.sets].filter((c): c is Count => c !== undefined).map((c) => `${countNumber(c)}x`).join("") : "";

  const words: string[] = [];
  const count = nested ? amountText(item) : countText(item);
  if (count !== "") words.push(count);
  const name = run?.name ?? item.name.trim();
  // `very light tempo`: the effort leads a run only when a kind word follows it; on its own the reader
  // would take it for the run's name, so it goes in the brackets.
  const effortLeads = run !== null && item.effort !== undefined && "word" in item.effort && /^(sprint|accel|tempo|resisted|skip|slow build|tt)\b/i.test(name);
  if (effortLeads) words.push((item.effort as { word: string }).word);
  if (name !== "") words.push(name);
  if (item.kind === "hurdles" && item.hurdles !== undefined) {
    const at = hurdleAt(item.hurdles);
    if (at !== "") words.push(at);
  }
  if (item.effort !== undefined && "percent" in item.effort) words.push(percentText(item.effort));
  let body = words.join(" ");
  const bracket = bracketParts(item, effortLeads);
  if (bracket.length > 0) body += ` (${bracket.join(", ")})`;
  if (item.perSide) body += "*";
  const notes: string[] = [];
  if (item.kind === "hurdles" && item.hurdles?.low !== undefined) notes.push(item.hurdles.low);
  for (const note of item.dashNotes ?? []) if (note.trim() !== "") notes.push(note.trim());
  for (const note of notes) body += ` - ${note}`;

  let line = nested ? `${outer}(${body})` : body;
  const rest = restText(item.rest);
  if (rest !== null) line += ` - ${rest}`;
  if (item.target !== undefined && item.target.trim() !== "") line += ` - target is ${item.target.trim()}`;
  if (item.onlyFor !== undefined && item.onlyFor.trim() !== "") line = `${item.onlyFor.trim()}: ${line}`;
  return line;
}

/** The definition's lines, in his current style (DESIGN.md §25.2). */
export function renderRows(rows: Row[]): string[] {
  const out: string[] = [];
  let inSection = false;
  let afterListRounds = false;
  for (const row of rows) {
    // An exercise not filled in yet (a row just added) writes nothing, rather than a bare `-`.
    if (row.type === "item" && blank(row.item)) continue;
    const bullet = inSection ? "  - " : "";
    // A line with no sets of its own after a round's members joins the round (the reader's rule, and his
    // `- 12 lateral leg raises` slip). A blank line keeps it apart, except under Prep: or Main:, where
    // a blank ends the section, so there it says its one set: `1x9 skater jumps`.
    let own: Row = row;
    if (afterListRounds && joinsRoundAbove(row)) {
      if (!inSection) out.push("");
      else if (row.type === "item") own = { ...row, item: { ...row.item, sets: { n: 1 } } };
    }
    afterListRounds = false;
    const r = own;
    switch (r.type) {
      case "section":
        out.push(`${r.name}:`);
        inSection = true;
        break;
      case "item":
        out.push(bullet + renderItem(r.item));
        pushSubNotes(out, r.item, bullet === "" ? "  " : "    ");
        break;
      case "rounds": {
        const head = roundsHead(r.sets, r.effort, r.note);
        // `5 sets (A + B)` has no room for the round's effort or note (the reader reads one bracket), and
        // the reader takes `3-4 sets (A + B)` for nothing at all.
        const plain = r.effort === undefined && (r.note ?? "").trim() === "" && r.sets.upper === undefined;
        const filled = r.items.filter((i) => !blank(i));
        if (r.style === "inline" && plain && filled.length >= 2 && filled.every((i) => (i.subNotes?.length ?? 0) === 0)) {
          out.push(`${bullet}${head} (${filled.map(renderItem).join(" + ")})`);
          break;
        }
        // Under Prep: or Main:, the reader takes a bulleted `- 2-4 sets` for a round only when it comes
        // first; `4 sets (2-4)` reads as `2-4 sets` does anywhere: 4 rounds, noted 2-4.
        out.push(bullet + (inSection && r.sets.upper !== undefined ? roundsHead({ n: r.sets.upper }, r.effort, r.note, countNumber(r.sets)) : head));
        const member = inSection ? "    - " : "  - ";
        // A note line under a member makes the reader take every later line into the round, so a
        // member's notes go after a dash on its own line, which the reader reads as the same note.
        for (const item of r.items) {
          if (blank(item)) continue;
          const notes = (item.subNotes ?? []).map((n) => n.trim()).filter((n) => n !== "");
          out.push(member + renderItem(notes.length ? { ...item, dashNotes: [...(item.dashNotes ?? []), ...notes], subNotes: [] } : item));
        }
        afterListRounds = true;
        break;
      }
      case "rest":
        out.push(`${bullet}${duration(r.seconds)} rest`);
        break;
      case "instruction":
        out.push((r.under ? "  " : "") + r.text.trim());
        break;
      case "reference":
        out.push(bullet + r.label.trim());
        break;
      case "written":
        out.push(r.text);
        break;
    }
  }
  return out;
}

function blank(item: Item): boolean {
  return renderItem(item).trim() === "";
}

function joinsRoundAbove(row: Row): boolean {
  if (row.type === "item") return row.item.sets === undefined && row.item.rounds === undefined;
  return row.type === "written" || row.type === "instruction" || row.type === "rest" || row.type === "reference";
}

function pushSubNotes(out: string[], item: Item, indent: string): void {
  for (const note of item.subNotes ?? []) if (note.trim() !== "") out.push(indent + note.trim());
}

export function countText(item: Item): string {
  const parts: string[] = [];
  if (item.rounds !== undefined) parts.push(countNumber(item.rounds));
  if (item.sets !== undefined) parts.push(countNumber(item.sets));
  if (item.amount !== undefined) parts.push(amountText(item));
  if (parts.length === 0) return "";
  // `2x Hanging knee raises`: sets with nothing counted in each.
  if (item.amount === undefined) return `${parts.join("x")}x`;
  // `4-6 x 30m`, as he spaces a range of sets.
  const ranged = [item.rounds, item.sets].some((c) => c?.upper !== undefined);
  return parts.join(ranged ? " x " : "x");
}

function amountText(item: Item): string {
  const amount = item.amount!;
  if (amount.parts !== undefined && amount.parts.length > 1) {
    const between = amount.between?.trim();
    return `(${amount.parts.map((p, i) => `${number(p)}${i === 0 ? "m" : ""}`).join(between ? `+${between}+` : "+")})`;
  }
  const unit = amount.unit === "reps" ? "" : amount.unit === "laps" ? " laps" : amount.unit;
  return countNumber(amount) + unit;
}

export function countNumber(count: Count): string {
  return count.upper !== undefined && count.upper !== count.n ? `${number(count.n)}-${number(count.upper)}` : number(count.n);
}

function number(value: number): string {
  return Number.isInteger(value) ? String(value) : String(Math.round(value * 100) / 100);
}

function roundsHead(sets: Count, effort: Effort | undefined, note: string | undefined, range?: string): string {
  const bracket: string[] = [];
  if (effort !== undefined) bracket.push("word" in effort ? effort.word : percentText(effort));
  if (note !== undefined && note.trim() !== "") bracket.push(note.trim());
  // Last: a bracket that starts with a number is read as a count.
  if (range !== undefined) bracket.push(range);
  return `${countNumber(sets)} sets` + (bracket.length > 0 ? ` (${bracket.join(", ")})` : "");
}

function percentText(effort: Extract<Effort, { percent: Count }>): string {
  return `${effort.how === "to" ? "to" : "@"} ${countNumber(effort.percent)}%` + (effort.of !== undefined && effort.of.trim() !== "" ? ` of ${effort.of.trim()}` : "");
}

function hurdleAt(h: Hurdles): string {
  const height = h.height?.trim();
  const discount = h.discount?.trim();
  const spacing = h.spacing?.trim();
  const parts: string[] = [];
  if (height && discount) parts.push(`${height} ${h.joiner ?? "with"} ${discount} discount`);
  else if (discount) parts.push(`${discount} discount`);
  else if (height) parts.push(height);
  if (spacing) parts.push(parts.length > 0 ? `and ${spacing} apart` : spacing);
  return parts.length > 0 ? `@ ${parts.join(" ")}` : "";
}

function bracketParts(item: Item, effortLed: boolean): string[] {
  const parts: string[] = [];
  const hurdles = item.kind === "hurdles" ? item.hurdles : undefined;
  if (hurdles?.steps !== undefined && hurdles.steps.trim() !== "") parts.push(hurdles.steps.trim());
  if (item.load !== undefined && (item.kind === "run" || item.kind === "drill" || item.kind === "hurdles")) parts.push(loadText(item.load));
  if (!effortLed && item.effort !== undefined && "word" in item.effort) parts.push(item.effort.word);
  for (const cue of item.cues ?? []) if (cue.trim() !== "") parts.push(cue.trim());
  if (item.rest?.reps !== undefined && item.rest.sets === undefined && "walkBack" in item.rest.reps) parts.push("walk-back rest");
  return parts;
}

function loadText(load: Load): string {
  if ("sled" in load) return `${load.sled} sled`;
  const words: string[] = [load.assisted ? "1080 assisted" : "1080"];
  if (load.kg !== undefined) words.push(`@ ${countNumber(load.kg)}kg`);
  if (load.decreasing !== undefined && load.decreasing.length > 0) words.push(`decreasing ${load.decreasing.map(number).join(",")}`);
  return words.join(" ");
}

function restText(rest: Rest | undefined): string | null {
  if (rest === undefined) return null;
  const { reps, sets } = rest;
  if (reps !== undefined && sets !== undefined) {
    // His `R: 2/8` is minutes; otherwise each keeps its unit (`R: 90s/6min`, `R: 1-2min/7min`).
    const bare = [reps, sets].every((v) => !("seconds" in v) || (v.seconds.upper === undefined && wholeMinutes(v.seconds)));
    return `R: ${restValue(reps, bare)}/${restValue(sets, bare)}`;
  }
  const only = reps ?? sets;
  if (only === undefined) return null;
  if ("walkBack" in only) return sets !== undefined ? "walk-back between sets" : null;
  if ("full" in only) return sets !== undefined ? "full rest between sets" : "full rest";
  return `${restValue(only, false)} ${sets !== undefined ? "between sets" : "rest"}`;
}

function restValue(value: RestValue, bareMinutes: boolean): string {
  if ("walkBack" in value) return "walk-back";
  if ("full" in value) return "full";
  const as = value.as ?? (bareMinutes ? "bare" : undefined);
  const minutes = countNumber({ n: value.seconds.n / 60, ...(value.seconds.upper !== undefined ? { upper: value.seconds.upper / 60 } : {}) });
  if (as === "bare") return minutes;
  if (as === "min") return `${minutes}min`;
  if (as === "s") return `${countNumber(value.seconds)}s`;
  return duration(value.seconds);
}

function wholeMinutes(seconds: Count): boolean {
  return seconds.n % 30 === 0 && (seconds.upper === undefined || seconds.upper % 30 === 0);
}

/** `90s`, `5min`, `1-2min`, `3.5min`. */
export function duration(seconds: Count): string {
  // He writes `45s`, `60s`, `90s`, then `2min`, `5min`.
  const minutes = seconds.n >= 120;
  if (!minutes) return `${countNumber(seconds)}s`;
  return `${countNumber({ n: seconds.n / 60, ...(seconds.upper !== undefined ? { upper: seconds.upper / 60 } : {}) })}min`;
}

